import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import styles from "./dropdown-new.module.scss";
import {
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  useContext,
  useRef,
} from "react";
import classNames from "classnames";
import { DropdownMenuLabelProps } from "@radix-ui/react-dropdown-menu";
import { handleItemNavigation } from "../filters/handleItemNavigation";
import * as PropTypes from "prop-types";
import { DropdownContext, DropdownProvider } from "./dropdown-new-context";

const DropdownTrigger = PopoverPrimitive.Trigger;
const DropdownArrow = PopoverPrimitive.Arrow;
export interface DropdownNewProps extends PopoverPrimitive.PopoverContentProps {
  includePortal?: boolean;
  loop?: boolean;
}

export const DropdownNew = ({
  children,
  ...rest
}: PropsWithChildren<PopoverPrimitive.PopoverProps>) => {
  return (
    <DropdownProvider
      open={rest.open}
      onOpenChange={rest.onOpenChange}
      modal={rest.modal}
    >
      <PopoverPrimitive.Root {...rest}>{children}</PopoverPrimitive.Root>
    </DropdownProvider>
  );
};

const DropdownContent = forwardRef<
  React.ElementRef<typeof PopoverPrimitive.PopoverContent>,
  DropdownNewProps
>((componentProps: PropsWithChildren<DropdownNewProps>, ref) => {
  const {
    children,
    className = undefined,
    includePortal = true,
    loop = true,
    ...rest
  } = componentProps;
  const classes = classNames(styles.content, className);
  const content = (
    <PopoverPrimitive.PopoverContent className={classes} {...rest} ref={ref}>
      {children}
    </PopoverPrimitive.PopoverContent>
  );

  return (
    <>
      {includePortal ? (
        <PopoverPrimitive.Portal forceMount={rest.forceMount}>
          {content}
        </PopoverPrimitive.Portal>
      ) : (
        <>{content}</>
      )}
    </>
  );
});
DropdownContent.displayName = "DropdownContent";
DropdownContent.propTypes = {
  className: PropTypes.string,
};
export interface DropdownNewItemProps {
  value: string | number | undefined;
  disabled?: boolean;
  onSelect?: (val: string | number | undefined) => void;
  className?: string;
  closeMenuAfterSelection?: boolean;
  customClass?: CSSProperties;
}

const DropdownItem = ({
  children,
  className,
  onSelect,
  disabled,
  value,
  customClass,
  closeMenuAfterSelection = true,
}: PropsWithChildren<DropdownNewItemProps>) => {
  const context = useContext(DropdownContext);

  const classes = classNames(className, {
    [styles.disabled]: disabled,
  });

  const ref = useRef<HTMLDivElement>(null);

  function handleClick(event: React.MouseEvent<HTMLDivElement>) {
    if (disabled || !onSelect) return;
    onSelect(value);
    if (closeMenuAfterSelection) {
      context?.onOpenChange?.(false);
    }
  }

  return (
    <div
      role="option"
      ref={ref}
      className={classes}
      onClick={handleClick}
      onKeyDown={(e) =>
        handleItemNavigation(
          e,
          ref,
          () => !disabled && onSelect && onSelect(value),
        )
      }
      tabIndex={disabled ? -1 : 0}
      data-disabled={disabled && true}
      style={customClass}
    >
      {children ?? value}
    </div>
  );
};

const DropdownLabel = ({ className, ...rest }: DropdownMenuLabelProps) => {
  const classes = classNames(className, styles.label);

  return <DropdownMenu.Label className={classes} {...rest} />;
};

const DropdownSeparator = () => {
  return (
    <DropdownMenu.Separator asChild>
      <div className={styles.delimiter}></div>
    </DropdownMenu.Separator>
  );
};

DropdownNew.Label = DropdownLabel;
DropdownNew.Item = DropdownItem;
DropdownNew.Separator = DropdownSeparator;
DropdownNew.Trigger = DropdownTrigger;
DropdownNew.Arrow = DropdownArrow;
DropdownNew.Content = DropdownContent;
